import { Text } from '@mantine/core';
import {
  IconBriefcase,
  IconCopy,
  IconMapDollar,
  IconPlayerPause,
  IconPlayerPlay,
} from '@tabler/icons-react';
import { useGetRecoveryPlans } from 'api/index';
import { Variable } from 'components/Variable';
import { tKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { flat, isNullish, map, pipe, prop } from 'remeda';
import { useLoadLightUsers } from 'shared/hooks/use-load-light-users';
import { useLoadReasons } from '~/modules/Settings/Variables/Reasons/api';
import type { AutomationAction, CreateAutomationVariables } from '../../../api';
import { useComputeFields } from '../../AutomationWizard';
import { ConditionActionBox } from './ConditionActionBox';

type ActionBoxProps = {
  action: AutomationAction;
  model: CreateAutomationVariables['targetModel'];
};

export const ActionRow = ({ action, model }: ActionBoxProps) => {
  const { t } = useTranslation();

  const { users = [] } = useLoadLightUsers();
  const { data: recoveryPlans } = useGetRecoveryPlans();
  const { reasons } = useLoadReasons({ reasonType: 'pause' });

  const fields = useComputeFields(model);
  const flattenedFields = pipe(fields, map(prop('items')), flat());

  if (action.type == null) return null;

  switch (action.type) {
    case 'CopyValueToField': {
      const destinationField = flattenedFields.find(f => f.value === action.destinationField);

      return (
        <ConditionActionBox>
          <IconCopy stroke={1.5} size={16} />
          <Text>{t(tKeys.AUTOMATION.ACTION_ROW.COPY)}</Text>
          <Variable color="coral">{`"${action.value}"`}</Variable>
          <Text>{t(tKeys.AUTOMATION.ACTION_ROW.IN)}</Text>
          <Variable color="blue">{destinationField?.label}</Variable>
        </ConditionActionBox>
      );
    }
    case 'CopyFieldToField': {
      const sourceField = flattenedFields.find(f => f.value === action.sourceField);
      const destinationField2 = flattenedFields.find(f => f.value === action.destinationField);

      return (
        <ConditionActionBox>
          <IconCopy stroke={1.5} size={16} />
          <Text>{t(tKeys.AUTOMATION.ACTION_ROW.COPY)}</Text>
          <Variable color="coral">{sourceField?.label}</Variable>
          <Text>{t(tKeys.AUTOMATION.ACTION_ROW.IN)}</Text>
          <Variable color="blue">{destinationField2?.label}</Variable>
        </ConditionActionBox>
      );
    }
    case 'AssignRecoveryPlan':
      return (
        <ConditionActionBox>
          <IconMapDollar stroke={1.5} size={16} />
          <Text>{t(tKeys.AUTOMATION.ACTION_ROW.SET_WORKFLOW_TO)}</Text>
          <Variable color="coral">
            {recoveryPlans?.data.find(p => p.id === action.assignableId)?.attributes.name}
          </Variable>
        </ConditionActionBox>
      );
    case 'AssignAccountManager': {
      const accountManager = users.find(u => String(u.id) === action.assignableId);
      if (isNullish(accountManager)) return null;
      return (
        <ConditionActionBox>
          <IconBriefcase stroke={1.5} size={16} />
          <Text>{t(tKeys.TASK.ACTIONS.ASSIGN)}</Text>
          <Variable color="coral">{accountManager?.name.full}</Variable>
          <Text>{t(tKeys.AUTOMATION.ACTION_ROW.AS_ACCOUNT_MANAGER)}</Text>
        </ConditionActionBox>
      );
    }
    case 'Pause': {
      const pauseReason = reasons.data.find(reason => String(reason.id) === action.assignableId);

      return (
        <ConditionActionBox>
          <IconPlayerPause stroke={1.5} size={16} />
          <Text>{t(tKeys.AUTOMATION.ACTION_ROW.PAUSE_CLIENT_FOR)}</Text>
          <Variable color="coral">{pauseReason?.name}</Variable>
          {Number(action.value) > 0 && (
            <>
              <Text>{t(tKeys.AUTOMATION.ACTION_ROW.DURING)}</Text>
              <Variable color="blue">{t(tKeys.DAYS, { count: Number(action.value) })}</Variable>
            </>
          )}
        </ConditionActionBox>
      );
    }
    case 'Unpause':
      return (
        <ConditionActionBox>
          <IconPlayerPlay stroke={1.5} size={16} />
          <Text>{t(tKeys.AUTOMATION.ACTION_ROW.UNPAUSE_CLIENT)}</Text>
        </ConditionActionBox>
      );
    default:
      return (
        <ConditionActionBox>
          <Text c="dimmed">{t(tKeys.AUTOMATION.ACTION_ROW.COULD_NOT_LOAD)}</Text>
        </ConditionActionBox>
      );
  }
};
